/**
 * Dev Console — Companies (ADR-023).
 *
 * What only Fias can give a partner Company — its own branding, and email from
 * its own domains (`PartnerCompanies`, one row per Company, one separate grant
 * per chip) — and the Fias side of that email: every live sending domain with
 * its seven-day health, why it is paused and by whom, whether its approval was
 * withdrawn (and when it retires), per-domain pause/resume, and the global
 * kill switch that stops ALL Company email without touching fias.io mail.
 *
 * Every act needs a reason and is audited server-side. Resuming is always a
 * human decision: automatic pauses (our thresholds, or SES disabling a
 * Company's tenant) never lift on their own.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { api, ApiError, type AdminCompanySendingDomainDto } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import { PartnerCompanies } from './PartnerCompanies';

/** Mirrors COMPANY_EMAIL_OFFBOARDING_GRACE_DAYS in @fias/db-types. */
const OFFBOARDING_GRACE_DAYS = 30;

type PendingAct =
  | { kind: 'pause' | 'resume'; domain: AdminCompanySendingDomainDto }
  | { kind: 'kill-switch'; paused: boolean };

function percent(n: number, of: number): string {
  if (of === 0) return '—';
  const p = (n / of) * 100;
  return `${p < 1 && p > 0 ? p.toFixed(2) : p.toFixed(1)}%`;
}

export function pausedByLabel(pausedBy: string | null | undefined): string | null {
  if (!pausedBy) return null;
  if (pausedBy === 'system:health') return 'Paused automatically (bounces/complaints)';
  if (pausedBy === 'system:ses') return 'Paused because SES disabled the tenant';
  if (pausedBy.startsWith('admin:')) return 'Paused by a Fias admin';
  return 'Paused';
}

function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.message.replace(/^[A-Z_]+:\s*/, '');
  return err instanceof Error ? err.message : 'Something went wrong. Please try again.';
}

/**
 * A query that failed, said as such with a way to try again — an outage must
 * never read as "nothing here" (no domains) or as a control that is merely
 * unavailable (the kill switch).
 */
function LoadError({
  what,
  error,
  onRetry,
}: {
  what: string;
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <p role="alert" className="text-sm text-destructive">
        Could not load {what}. {errorText(error)}
      </p>
      <Button size="sm" variant="outline" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

function retiresOn(orphanedAt: string): string {
  const d = new Date(orphanedAt);
  d.setDate(d.getDate() + OFFBOARDING_GRACE_DAYS);
  return d.toLocaleDateString();
}

export function CompaniesPage() {
  const qc = useQueryClient();
  const domains = useQuery({
    queryKey: queryKeys.adminCompanies.domains(),
    queryFn: () => api.adminListCompanySendingDomains(),
  });
  const killSwitch = useQuery({
    queryKey: queryKeys.adminCompanies.killSwitch(),
    queryFn: () => api.adminGetCompanyEmailKillSwitch(),
  });
  const [pending, setPending] = useState<PendingAct | null>(null);
  const [reason, setReason] = useState('');
  // Every way the dialog closes clears the reason, so the next act never
  // opens with (and records in the audit log) the previous one's reason.
  const closeDialog = () => {
    setPending(null);
    setReason('');
  };

  const act = useMutation({
    mutationFn: async (p: PendingAct) => {
      const why = reason.trim();
      if (p.kind === 'kill-switch') return api.adminSetCompanyEmailKillSwitch(p.paused, why);
      return p.kind === 'pause'
        ? api.adminPauseCompanySendingDomain(p.domain.domainId, why)
        : api.adminResumeCompanySendingDomain(p.domain.domainId, why);
    },
    onSuccess: () => {
      closeDialog();
      void qc.invalidateQueries({ queryKey: queryKeys.adminCompanies.all });
    },
    onError: (err) =>
      toast({
        title: 'Could not save',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      }),
  });

  const paused = killSwitch.data?.killSwitch.paused === true;

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-8">
      <div className="mb-8">
        <Link
          to="/dev-console"
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Dev Console
        </Link>
        <h1 className="text-3xl font-bold text-foreground">Companies</h1>
        <p className="mt-2 text-muted-foreground">
          What partner Companies may do under their own name: their branding, and email from their
          own domains. fias.io mail is never affected by anything on this page.
        </p>
      </div>

      <PartnerCompanies />

      <h2 className="mb-3 mt-8 text-lg font-semibold text-foreground">Email sending</h2>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            All Company email
            {killSwitch.data && (
              <Badge variant={paused ? 'destructive' : 'secondary'}>
                {paused ? 'Paused' : 'Sending'}
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Stops every Company&apos;s mail at once. Arches and new signature requests are refused;
            Document Sign sends updates to people already on an envelope from fias.io.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {paused && killSwitch.data?.killSwitch.reason && (
            <p className="text-sm text-muted-foreground">
              Reason: {killSwitch.data.killSwitch.reason}
            </p>
          )}
          {killSwitch.isError ? (
            <LoadError
              what="whether Company email is paused"
              error={killSwitch.error}
              onRetry={() => void killSwitch.refetch()}
            />
          ) : (
            <Button
              variant={paused ? 'default' : 'destructive'}
              disabled={!killSwitch.data}
              onClick={() => setPending({ kind: 'kill-switch', paused: !paused })}
            >
              {paused ? 'Resume all Company email' : 'Pause all Company email'}
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sending domains</CardTitle>
          <CardDescription>
            Last 7 days. Automatic pauses never lift on their own — resume after checking.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {domains.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : domains.isError ? (
            <LoadError
              what="sending domains"
              error={domains.error}
              onRetry={() => void domains.refetch()}
            />
          ) : (domains.data?.domains.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No Company sending domains yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company / domain</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Sent</TableHead>
                  <TableHead className="text-right">Bounced</TableHead>
                  <TableHead className="text-right">Spam</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {domains.data!.domains.map((d) => (
                  <TableRow key={d.domainId}>
                    <TableCell>
                      <div className="font-medium">{d.domain}</div>
                      <div className="text-xs text-muted-foreground">{d.companyName}</div>
                    </TableCell>
                    <TableCell className="max-w-xs space-y-1">
                      <Badge variant={d.status === 'paused' ? 'destructive' : 'secondary'}>
                        {d.status}
                      </Badge>
                      {d.status === 'paused' && (
                        <div className="text-xs">{pausedByLabel(d.pausedBy)}</div>
                      )}
                      {d.statusReason && (
                        <div className="text-xs text-muted-foreground">{d.statusReason}</div>
                      )}
                      {d.orphanedAt && (
                        <div className="text-xs text-destructive">
                          Approval withdrawn — retires {retiresOn(d.orphanedAt)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{d.stats?.sent ?? 0}</TableCell>
                    <TableCell className="text-right">
                      {d.stats ? percent(d.stats.bounced, d.stats.settled) : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {d.stats ? percent(d.stats.complained, d.stats.settled) : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {d.status === 'verified' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPending({ kind: 'pause', domain: d })}
                        >
                          Pause
                        </Button>
                      )}
                      {d.status === 'paused' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPending({ kind: 'resume', domain: d })}
                        >
                          Resume
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{pending ? dialogTitle(pending) : ''}</DialogTitle>
            <DialogDescription>
              A reason is required. It is recorded in the security audit log.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="company-email-reason">Reason</Label>
            <Textarea
              id="company-email-reason"
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              disabled={reason.trim().length === 0 || act.isPending}
              onClick={() => pending && act.mutate(pending)}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function dialogTitle(p: PendingAct): string {
  if (p.kind === 'kill-switch') {
    return p.paused ? 'Pause all Company email?' : 'Resume all Company email?';
  }
  return p.kind === 'pause' ? `Pause ${p.domain.domain}?` : `Resume ${p.domain.domain}?`;
}
